import { useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { auth } from "../config/firebase";
import { userIsAdmin } from "../services/auth";
import SEO from "../components/ui/SEO";
import AdminPanel from "../components/ui/AdminPanel";
import { LoadingTerminal } from "../components/ui/LoadingState";
import "./Admin.css";

export default function AdminPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [useMagic, setUseMagic] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [magicSending, setMagicSending] = useState(false);
  const [magicMessage, setMagicMessage] = useState("");

  // Check if user is already logged in on mount
  useEffect(() => {
    // If this is a sign-in-with-email-link callback, complete sign-in first.
    const tryCompleteEmailLinkSignIn = async () => {
      try {
        const url = window.location.href;
        if (isSignInWithEmailLink(auth, url)) {
          // Try to get the email from localStorage
          let storedEmail = window.localStorage.getItem("emailForSignIn");
          if (!storedEmail) {
            // Ask the user for the email they used
            storedEmail = window.prompt("Please provide your email for confirmation");
          }
          if (storedEmail) {
            await signInWithEmailLink(auth, storedEmail, url);
            window.localStorage.removeItem("emailForSignIn");
          }
        }
      } catch (e) {
        console.error("Error completing email link sign-in:", e);
      }
    };
    tryCompleteEmailLinkSignIn();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is already logged in, verify they have admin access
        if (await userIsAdmin(user)) {
          setAuthed(true);
          setEmail(user.email);
        } else {
          setError("You don't have admin access");
          await signOut(auth);
          setAuthed(false);
        }
      } else {
        setAuthed(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [auth]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Sign in with Firebase (password)
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Verify admin access via custom claims (rules enforce this server-side)
      if (!(await userIsAdmin(user))) {
        setError("You don't have admin access");
        await signOut(auth);
        setAuthed(false);
      } else {
        setAuthed(true);
      }
    } catch (e) {
      console.error("[Admin Login] Full error:", {
        code: e.code,
        message: e.message,
        email,
        serverTimestamp: new Date().toISOString(),
        fullError: e,
      });
      
      if (e.code === "auth/user-not-found") {
        setError("User not found. Email/Password auth may not be enabled in Firebase.");
      } else if (e.code === "auth/wrong-password") {
        setError("Invalid email or password");
      } else if (e.code === "auth/too-many-requests") {
        setError("Too many login attempts. Please try again later.");
      } else if (e.code === "auth/invalid-email") {
        setError("Invalid email address");
      } else {
        setError(`Login failed: ${e.message}`);
      }
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const sendMagicLink = async (e) => {
    e && e.preventDefault();
    setError("");
    setMagicMessage("");
    setMagicSending(true);
    try {
      const actionCodeSettings = {
        // After sign-in completes, user will be redirected to /admin
        url: window.location.origin + "/admin",
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      // Save the email locally to complete sign-in when the link is opened
      window.localStorage.setItem("emailForSignIn", email);
      setMagicMessage("Magic link sent — check your email.");
    } catch (e) {
      console.error("Error sending magic link:", e);
      setError(e.message || "Failed to send magic link");
    } finally {
      setMagicSending(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setAuthed(false);
      setEmail("");
      setPassword("");
      setError("");
    } catch (e) {
      console.error("Logout error:", e);
      setError("Error logging out");
    }
  };

  if (loading) {
    return (
      <section className="admin-login admin-login--loading">
        <SEO title="Admin" description="Admin panel for Rajat Shenoi's portfolio." url="/admin" noindex />
        <div className="container admin-login__loading-shell">
          <LoadingTerminal
            title="admin.auth"
            className="admin-login__loading-card"
            lines={[
              { key: "admin-headline", variant: "headline" },
              { key: "admin-subtitle", variant: "title" },
              { key: "admin-line-1", variant: "text" },
              { key: "admin-line-2", variant: "text", style: { width: "72%" } },
            ]}
            actions={[
              { key: "admin-action-1", style: { width: "7.5rem" } },
              { key: "admin-action-2", ghost: true, style: { width: "6rem" } },
            ]}
            meta={<span>Checking your session and admin claims.</span>}
          />
        </div>
      </section>
    );
  }

  if (!authed) {
    return (
      <section
        className="admin-login"
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100vw",
          minHeight: "100dvh",
          padding: "1rem",
          boxSizing: "border-box",
        }}
      >
        <SEO title="Admin Login" description="Admin panel for Rajat Shenoi's portfolio." url="/admin" noindex />
        <div
          className="card admin-login__card"
          style={{ width: "320px", maxWidth: "320px", flex: "0 0 auto" }}
        >
          <h2 className="admin-login__title">Admin Login</h2>
          <fieldset className="admin-login__mode" aria-label="Sign-in method">
            <label className={`admin-login__mode-option ${!useMagic ? "is-active" : ""}`}>
              <input
                type="radio"
                name="loginMode"
                checked={!useMagic}
                onChange={() => setUseMagic(false)}
              />
              <span>Password</span>
            </label>
            <label className={`admin-login__mode-option ${useMagic ? "is-active" : ""}`}>
              <input
                type="radio"
                name="loginMode"
                checked={useMagic}
                onChange={() => setUseMagic(true)}
              />
              <span>Email link</span>
            </label>
          </fieldset>
          <p className="admin-login__hint">Sign in with Firebase to manage content.</p>
          <form
            className="admin-login__form"
            onSubmit={handleLogin}
          >
            <input
              className="admin-input"
              placeholder="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
              autoComplete="email"
              disabled={loading}
            />
            {!useMagic ? (
              <input
                className="admin-input"
                placeholder="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={loading}
              />
            ) : (
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <button className="btn btn-primary" onClick={sendMagicLink} disabled={magicSending || !email}>
                  {magicSending ? "Sending..." : "Send magic link"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => { setEmail(""); setError(""); setMagicMessage(""); }}
                  disabled={magicSending}
                >
                  Clear
                </button>
              </div>
            )}
            {error && <div style={{ color: "crimson" }}>{error}</div>}
            {magicMessage && <div style={{ color: "var(--c-primary)", marginTop: "0.5rem" }}>{magicMessage}</div>}
            <div className="admin-login__actions">
              {!useMagic && (
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? "Signing in..." : "Sign In"}
                </button>
              )}
            </div>
          </form>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO title="Admin" description="Admin panel for Rajat Shenoi's portfolio." url="/admin" noindex />
      <AdminPanel auth={auth} email={email} onLogout={handleLogout} pageMode={true} />
    </>
  );
}
